import groq from 'groq';
import sanityRequest from '@/sanity/requests/base';
import { getImageAttributes, slugToLink } from '@/sanity';
import { Story, StoryListing, WorkListing } from '@/types';
import { SanityImageObject } from '@sanity/image-url/lib/types/types';

const query = groq`*[ _type == "story" && slug.current == $slug][0] {
  _id,
  title,
  titleTop,
  titleBottom,
  metaDescription,
  "slug": slug.current,
  author,
  publishedDate,
  hero {
    "desktop": desktopImage,
    "mobile": mobileImage,
    attribution,
    lightText,
  },
  content,
  "works": *[_type == "work" && references(^.artists[]._ref)] | order(order asc) {
    _id,
    title,
    "slug": slug.current,
    year,
    price,
    artist->{_id, name},
    "thumb": images[0]
  },
}`;

const blockTextToStr = (blocks: any) =>
  (blocks || [])
    .find(({ _type }: any) => _type === 'block')
    ?.children.filter(({ _type }: any) => _type === 'span')
    .map(({ text }: any) => text)
    .join('') || '';

export default function getStory(slug: string) {
  const { sanity, store, loading, data, setData, setMeta } = sanityRequest<
    Story | StoryListing
  >();

  const story = store.state.stories[slug];
  if (story) {
    setData(story);
    setMeta({
      title: blockTextToStr(story.title),
      description: 'metaDescription' in story ? story.metaDescription : '',
    });
  }
  if (!story || !('content' in story)) {
    sanity.client
      .fetch<Story<SanityImageObject>>(query, {
        slug,
      })
      .then((res) => {
        const { attribution, lightText } = res.hero;
        const {
          _id,
          title,
          titleTop,
          titleBottom,
          metaDescription,
          slug,
          author,
          content,
          publishedDate,
        } = res;
        const link = slugToLink('story', res.slug);
        const hero = {
          desktop: getImageAttributes(res.hero.desktop, 2560, [
            1366,
            1600,
            1920,
            2560,
          ]),
          mobile: getImageAttributes(res.hero.mobile, 1366, [
            640,
            768,
            1024,
            1366,
          ]),
          attribution,
          lightText,
        };
        const works = res.works.map((work) => {
          const transformed: WorkListing = {
            _id: work._id,
            title: work.title,
            slug: work.slug,
            price: work.price,
            year: work.year,
            artist: work.artist,
            thumb: getImageAttributes(work.thumb),
          };
          return transformed;
        });

        const story: Story = {
          _id,
          title,
          titleTop,
          titleBottom,
          metaDescription,
          slug,
          author,
          content,
          link,
          hero,
          works,
          publishedDate,
        };
        store.commit.setStory(story);
        setData(store.state.stories[slug]);
        setMeta({
          title: blockTextToStr(story.title),
          description: story.metaDescription,
        });
      });
  }

  return { data, loading };
}
